import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";

export default function Privacy() {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-onyx-900 text-white">
            <Seo
                title="Privacy Policy — SRT Finder Marketplace"
                description="SRT Finder Privacy Policy. How we collect, use, and protect your data when you buy, sell, or trade Dodge Hellcat, SRT, R/T, TRX, and RHO vehicles on the platform."
                canonicalPath="/privacy"
            />
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <Logo size="xs" />
                </div>
            </div>
            <article className="max-w-3xl mx-auto px-5 py-8 space-y-6 text-onyx-300 leading-relaxed">
                <header>
                    <div className="label-caps text-hellcat">Legal</div>
                    <h1 className="headline text-4xl mt-2">Privacy Policy</h1>
                    <p className="text-xs text-onyx-500 mt-2">Last updated: February 2026</p>
                </header>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">1. What we collect</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>Account info: name, email, password hash, avatar, bio, location you enter.</li>
                        <li>Content: listings, community posts, comments, messages, photos, ratings, saved searches you create.</li>
                        <li>Usage: pages you view, listings you save, votes you cast, technical logs (IP, user agent, request timestamps).</li>
                        <li>If you sign in with Google via Emergent-managed Google OAuth, we receive your name, email, and profile photo.</li>
                    </ul>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">2. How we use it</h2>
                    <ul className="list-disc pl-6 space-y-1">
                        <li>To run the platform: show you listings, let you post, message other users, save searches, rate sellers.</li>
                        <li>To keep the community safe: detect fraud, spam, harassment, and V6 violators.</li>
                        <li>To operate the service: fix bugs, improve performance, and communicate about your account.</li>
                    </ul>
                    <p>We do not sell your personal data.</p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">3. What's public</h2>
                    <p>
                        Your profile (name, avatar, bio, location, garage, active listings,
                        community posts and comments, seller rating) is public to anyone who visits
                        the platform. Private direct messages are visible only to the participants
                        and to platform admins for moderation.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">4. Cookies & sessions</h2>
                    <p>
                        We use httpOnly, secure cookies to keep you logged in. We also use a token
                        stored in your browser's local storage as a fallback for browsers that
                        aggressively block third-party cookies. Both expire and can be revoked by
                        signing out.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">5. Third parties</h2>
                    <p>
                        We use trusted infrastructure providers to run the service (hosting,
                        object storage for photos, Google OAuth). They only see the data required
                        to serve you. We may add optional analytics later; this policy will be
                        updated if we do.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">6. Your rights</h2>
                    <p>
                        You can edit your profile, delete your vehicles / listings / posts /
                        messages at any time, or request full account deletion by contacting
                        support. Deletion removes your account and content within 30 days, except
                        where retention is legally required.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">7. Children</h2>
                    <p>
                        SRT Finder is not intended for anyone under 18. We do not knowingly
                        collect information from minors.
                    </p>
                </section>

                <section className="space-y-2">
                    <h2 className="headline text-xl text-white">8. Contact</h2>
                    <p>
                        Privacy questions?{" "}
                        <a className="text-hellcat" href="mailto:privacy@srtfinder.com">
                            privacy@srtfinder.com
                        </a>
                        .
                    </p>
                </section>

                <div className="pt-6">
                    <Link to="/terms" className="text-hellcat label-caps text-xs">
                        Read Terms of Service →
                    </Link>
                </div>
            </article>
        </div>
    );
}
